import Styles from './deadlinePicker.module.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IoCalendarOutline, IoChevronBack, IoChevronForward } from 'react-icons/io5';

type Props = {
  value: string; // 'YYYY-MM-DD' か ''
  min?: string;
  max?: string;
  canClear: boolean;
  label: string; // 読み上げ用(例: 第1回募集の締切日)
  onChange: (value: string) => void;
};

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];
const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (v: string) => {
  const [ y, m, d ] = v.split('-').map(Number);
  return new Date(y, m - 1, d);
};
export const formatDeadline = (v: string) => {
  const d = parse(v);
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日（${WEEKDAYS[d.getDay()]}）`;
};

// 募集回の締切日を選ぶ小さなカレンダー。ブラウザ標準の日付入力(年/月/日)は見た目がそろわないため、
// サロン見学の受付枠(18.visitInfo/CalendarPanel)と同じ見た目のカレンダーをポップアップで出す。
const DeadlinePicker: React.FC<Props> = ({ value, min, max, canClear, label, onChange }) => {
  const [ open, setOpen ] = useState(false);
  const [ month, setMonth ] = useState(() => {
    const base = value ? parse(value) : new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  const rootRef = useRef<HTMLDivElement | null>(null);
  const today = ymd(new Date());

  // 開くたびに、選んでいる日(無ければ今月)の月を出す
  useEffect(() => {
    if (!open) return;
    const base = value ? parse(value) : new Date();
    setMonth(new Date(base.getFullYear(), base.getMonth(), 1));
  }, [open, value]);

  // 外側を押す・Esc で閉じる
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const cells = useMemo(() => {
    const y = month.getFullYear();
    const m = month.getMonth();
    const list: (string | null)[] = [];
    for (let i = 0; i < new Date(y, m, 1).getDay(); i++) list.push(null);
    for (let d = 1; d <= new Date(y, m + 1, 0).getDate(); d++) list.push(ymd(new Date(y, m, d)));
    return list;
  }, [month]);

  const moveMonth = (diff: number) => setMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + diff, 1));
  const prevDisabled = !!min && ymd(new Date(month.getFullYear(), month.getMonth(), 0)) < min;
  const nextDisabled = !!max && ymd(new Date(month.getFullYear(), month.getMonth() + 1, 1)) > max;

  return (
    <div className={Styles.root} ref={rootRef}>
      <button
        type="button"
        className={value ? Styles.trigger : `${Styles.trigger} ${Styles.triggerEmpty}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={label}
        aria-expanded={open}
      >
        <IoCalendarOutline />
        {value ? formatDeadline(value) : '締切日を設定'}
      </button>
      {canClear && value && (
        <button type="button" className={Styles.unset} onClick={() => { setOpen(false); onChange(''); }}>
          未設定にする
        </button>
      )}

      {open && (
        <div className={Styles.popover} role="dialog" aria-label={label}>
          <div className={Styles.nav}>
            <button type="button" className={Styles.navBtn} onClick={() => moveMonth(-1)} disabled={prevDisabled} aria-label="前の月">
              <IoChevronBack />
            </button>
            <span className={Styles.month}>{month.getFullYear()}年{month.getMonth() + 1}月</span>
            <button type="button" className={Styles.navBtn} onClick={() => moveMonth(1)} disabled={nextDisabled} aria-label="次の月">
              <IoChevronForward />
            </button>
          </div>
          <div className={Styles.grid}>
            {WEEKDAYS.map((w, i) => (
              <span key={w} className={i === 0 ? `${Styles.weekday} ${Styles.sun}` : i === 6 ? `${Styles.weekday} ${Styles.sat}` : Styles.weekday}>{w}</span>
            ))}
            {cells.map((date, i) => {
              if (!date) return <span key={`e${i}`} />;
              const disabled = (!!min && date < min) || (!!max && date > max);
              const classes = [
                Styles.day,
                date === value ? Styles.daySelected : '',
                date === today ? Styles.dayToday : '',
                date < today ? Styles.dayPast : '',
              ].filter(Boolean).join(' ');
              return (
                <button
                  key={date}
                  type="button"
                  className={classes}
                  disabled={disabled}
                  onClick={() => { onChange(date); setOpen(false); }}
                >
                  {Number(date.slice(8))}
                </button>
              );
            })}
          </div>
          <div className={Styles.footer}>
            <span>この日の23:59まで受け付けます</span>
            {canClear && value && (
              <button type="button" className={Styles.clear} onClick={() => { setOpen(false); onChange(''); }}>未設定にする</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeadlinePicker;
